import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { ConfigService, TranslateService } from '@icc/shared';
import { logger, ToastService } from '@icc/shared/helpers';
import { BehaviorSubject, Observable, ReplaySubject, filter, firstValueFrom, map } from 'rxjs';
import { PriceVersion } from '@icc/b2b/shared';
import * as Sentry from '@sentry/browser';

@Injectable({
    providedIn: 'root'
})
export class PriceVersionService {

    allPriceVersions$ = new BehaviorSubject<PriceVersion[] | null>(null);
    loaded$ = new ReplaySubject<true>();
    _loaded = false;
    activatedPriceVersion$ = new BehaviorSubject<PriceVersion | null>(null);
    actualPriceVersion$ = new BehaviorSubject<PriceVersion | null>(null);
    activatedPriceVersion: PriceVersion;
    isActivatedPriceVersionActual: boolean;
    actualPriceVersion: PriceVersion;
    allPriceVersions: PriceVersion[] = [];

    constructor(
        private http: HttpClient,
        private configService: ConfigService,
        private toastService: ToastService,
        private translateService: TranslateService
    ) {
        window.setInterval(this.checkActivePriceVersion.bind(this), 60000);
    }

    public async updatePriceVersions(priceVersions: PriceVersion[]) {
        const priceVersionsMap = new Map(
            this.configService?.config?.userData?.user?.userBoxData?.priceVersions?.map(
                pv => [pv.id, pv.name]
            ) || []
        );
        const priceVersionsTranslated = priceVersions.map(item => {
            return {
                ...item,
                name: priceVersionsMap.get(item.id) || item.name
            }
        });
        this.allPriceVersions = priceVersionsTranslated;
        this.allPriceVersions$.next(priceVersionsTranslated);
        await this.loadActivePriceVersionFromSession();
        const result = await this.checkActivePriceVersion();
        if (result) {
            logger.log('Price versions updated');
            this._loaded = true;
            this.loaded$.next(true);
        } else {
            logger.warn('Missing price versions');
        }
    }

    public async loadActivePriceVersionFromSession() {
        this.actualPriceVersion = this.getPriceVersion(Number(this.configService.user?.user?.ActualPriceVersionId));
        this.activatedPriceVersion = this.getPriceVersion(Number(this.configService.user?.user?.PriceVersionId));
        this.actualPriceVersion$.next(this.actualPriceVersion);
        this.activatedPriceVersion$.next(this.activatedPriceVersion);
    }

    public activatePriceVersionData(id: number) {
        return new Promise<void>((resolve,reject) => {
            this.toastService.add({
                title: this.translateService.instant('PRICE_VERSION|Zmiana wersji cenowej',),
                body:  this.translateService.instant(
                    'PRICE_VERSION|Rozpoczęto zmianę wersji cenowej'
                ),
                severity: 'info'
            });
            this.http.get<PriceVersion[]>(`${window.location.origin}/api/b2b/price-versions/activate/${id}`).toPromise().then(() => {
                const activatedVersion = this.allPriceVersions.find(item=>item.id === +id);
                if (activatedVersion) {
                    window.localStorage.setItem(`activePriceVersion-${this.configService?.user?.user?.id}`,JSON.stringify(activatedVersion));
                    this.activatedPriceVersion = activatedVersion;
                    this.activatedPriceVersion$.next(activatedVersion);
                    this.toastService.add({
                        title: this.translateService.instant('PRICE_VERSION|Zmiana wersji cenowej',),
                        body:  this.translateService.instant(
                            'PRICE_VERSION|Zmiana wersji cenowej zakończyła się powodzeniem'
                        ),
                        severity: 'success'
                    });
                    resolve();
                } else {
                    reject();
                }
            });
        })
    }

    public async checkActivePriceVersion() {
        if (!this.allPriceVersions.length) {
            return false;
        }
        let activatedVersionId = this.configService?.user?.user?.id ? Number(window.localStorage.getItem(`activePriceVersionId-${this.configService?.user?.user?.id}`)) : null;
        let activatedVersion = this.getPriceVersion(activatedVersionId);
        const now = new Date();
        const currentActualVersion = this.getCurrentActualPriceVersion(now);
        let actualVersion = this.actualPriceVersion;

        if (!actualVersion || !this.matchTime(actualVersion, now)) {
            const oldActualVersion = actualVersion;
            actualVersion = currentActualVersion;
            this.actualPriceVersion = actualVersion;
            this.actualPriceVersion$.next(actualVersion);
            if (oldActualVersion && oldActualVersion?.id !== actualVersion?.id) {
                this.toastService.add({
                    title: this.translateService.instant('PRICE_VERSION|Nowa aktualna wersja cenowa',),
                    body:  this.translateService.instant(
                        'PRICE_VERSION|Aktualna wersja cenowa uległa zmianie ze względu na przekroczenie daty końca obowiązywania'
                    ),
                    severity: 'warn'
                });
            }
        }

        if ((!activatedVersion?.hasAccess || !this.matchTime(activatedVersion, now)) && actualVersion?.hasAccess) {
            const oldActivatedVersion = activatedVersion;
            activatedVersionId = actualVersion?.id;
            activatedVersion = actualVersion;
            this.activatedPriceVersion = activatedVersion;
            this.activatedPriceVersion$.next(activatedVersion);
            if (oldActivatedVersion && oldActivatedVersion?.id !== activatedVersion?.id) {
                this.toastService.add({
                    title: this.translateService.instant('PRICE_VERSION|Nowa aktywna wersja cenowa',),
                    body:  this.translateService.instant(
                        'PRICE_VERSION|Aktywna wersja cenowa uległa zmianie ze względu na przekroczenie daty końca obowiązywania, i została zmieniona na obowiązującą według daty'
                    ),
                    severity: 'warn'
                });
                await this.activatePriceVersionData(actualVersion?.id);
            }
            Sentry.setTag('priceVersion', this.activatedPriceVersion?.id);
            window.localStorage.setItem(`activePriceVersionId-${this.configService?.user?.user?.id}`, String(activatedVersion.id));
        }
        return true;
    }

    getCurrentActualPriceVersion(now = new Date()) {
        return this.allPriceVersions.find(
            (item) => this.matchTime(item, now) && item.hasAccess
        )
    }

    matchTime(priceVersion: PriceVersion, time: Date) {
        return (!priceVersion?.effectiveDate || time > new Date(priceVersion?.effectiveDate)) &&
            (!priceVersion?.expiredDate || time < new Date(priceVersion?.expiredDate))
    }

    getPriceVersion(id: number) {
        return this.allPriceVersions?.find(
            (item) => item?.id === id
        );
    }

    deleteLocalStorageData(){
        window.localStorage.removeItem(`activePriceVersion-${this.configService?.user?.user?.id}`);
    }
}
